import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import XLSX from "xlsx-js-style"

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog"

import {
  Card,
  CardContent,
} from "../components/ui/card"

import { Input } from "../components/ui/input"
import { Badge } from "../components/ui/badge"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"

import { Button } from "../components/ui/button"

import {
  getBookings,
  updateBooking,
  uploadDropProofAdmin,
  createAdminBooking,
} from "../components/api/adminApi";

import { toast } from "sonner"
import {
  ArrowLeft, FileDown, Filter, FileText,
  UserRound, HeartPulse, Ambulance, ClipboardList, ImageOff, Pencil, Camera, Plus, X
} from "lucide-react"

// ─────────────────── Default empty form template ───────────────────
const emptyForm = {
  booker_name: "",
  booker_phone: "",
  booking_date: new Date().toISOString().split("T")[0],
  booking_time: new Date().toTimeString().slice(0, 5),
  registration_number: "",
  ambulance_type: "Basic",
  patient_name: "",
  patient_contact: "",
  patient_age: "",
  patient_gender: "Male",
  patient_aadhar: "",
  pickup_address: "",
  drop_address: "",
  patient_village: "",
  patient_police_station: "",
  patient_district: "",
  patient_pincode: "",
  medical_condition: "",
  caretaker_name: "",
  caretaker_phone: "",
  caretaker_relation: "",
  // driver fields (nested object will be built on submit)
  driver_name: "",
  driver_phone: "",
  driver_vehicle_number: "",
  status: "completed",
}

export default function ReportPage() {
  const navigate = useNavigate()

  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [generatingCertId, setGeneratingCertId] = useState(null)
  const [reportType, setReportType] = useState("ambulance")
  const [selectedImage, setSelectedImage] = useState(null)

  const [editingBooking, setEditingBooking] = useState(null)
  const [savingEdit, setSavingEdit] = useState(false)
  const [editDropFile, setEditDropFile] = useState(null)

  // ─────────── NEW: Add form state ───────────
  const [showAddForm, setShowAddForm] = useState(false)
  const [addForm, setAddForm] = useState({ ...emptyForm })
  const [submittingAdd, setSubmittingAdd] = useState(false)
  const [addDropFile, setAddDropFile] = useState(null)

  const ambulanceLogo = "/ambulance_logo.png"
  const ambulancePhoto = "/ambulance.jpeg"
  const footerPhoto = "/footer.jpeg"

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8000";

  const [filters, setFilters] = useState({
    patient: "",
    mobile: "",
    driver: "",
    startDate: "",
    endDate: "",
  })

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `${API_BASE_URL}/${cleanPath}`;
  };

  const toBase64 = async (url) => {
    if (!url) return null;
    try {
      const res = await fetch(url, { mode: "cors" });
      if (!res.ok) return null;
      const blob = await res.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const blob = await res.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }) + " | " + date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    if (status === "pending") return "bg-amber-50 text-amber-700 border-amber-200";
    if (status === "assigned" || status === "online") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (status === "completed") return "bg-blue-50 text-blue-700 border-blue-200";
    return "bg-slate-50 text-slate-700 border-slate-200";
  }

  useEffect(() => {
    fetchReports()
  }, [])

  const fetchReports = async () => {
    try {
      const res = await getBookings()
      const data = Array.isArray(res)
        ? res
        : res?.data || res?.bookings || res?.results || []
      setBookings(data)
    } catch {
      toast.error("Failed to load reports")
    } finally {
      setLoading(false)
    }
  }

  const handleSaveEdit = async () => {
    if (!editingBooking) return;
    setSavingEdit(true);
    try {
      await updateBooking(editingBooking.id, editingBooking);

      if (editDropFile) {
        await uploadDropProofAdmin(editingBooking.id, editDropFile);
      }

      toast.success("Booking updated successfully");
      setEditingBooking(null);
      setEditDropFile(null);
      fetchReports();
    } catch (err) {
      toast.error("Failed to update booking");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditingBooking(prev => ({ ...prev, [field]: value }));
  };

  const openEditDialog = (booking) => {
    setEditingBooking(booking);
    setEditDropFile(null);
  }

  // ─────────── NEW: Add form handlers ───────────
  const handleAddChange = (field, value) => {
    setAddForm(prev => ({ ...prev, [field]: value }));
  };

  const resetAddForm = () => {
    setAddForm({ ...emptyForm, booking_date: new Date().toISOString().split("T")[0], booking_time: new Date().toTimeString().slice(0, 5) });
    setAddDropFile(null);
  };

 const handleAddSubmit = async () => {
  if (!addForm.patient_name.trim()) {
    toast.error("Patient Name is required");
    return;
  }

  setSubmittingAdd(true);
  try {
    // 1. Send full payload to backend
    const payload = {
      booker_name: addForm.booker_name || null,
      booker_phone: addForm.booker_phone || null,
      booking_date: addForm.booking_date || null,
      booking_time: addForm.booking_time || null,
      registration_number: addForm.registration_number || null,
      ambulance_type: addForm.ambulance_type || "Basic",
      patient_name: addForm.patient_name,
      patient_contact: addForm.patient_contact || null,
      patient_age: addForm.patient_age ? Number(addForm.patient_age) : null,
      patient_gender: addForm.patient_gender || null,
      patient_aadhar: addForm.patient_aadhar || null,
      patient_village: addForm.patient_village || null,
      patient_police_station: addForm.patient_police_station || null,
      patient_district: addForm.patient_district || null,
      patient_pincode: addForm.patient_pincode || null,
      medical_condition: addForm.medical_condition || null,
      pickup_address: addForm.pickup_address || null,
      drop_address: addForm.drop_address || null,
      caretaker_name: addForm.caretaker_name || null,
      caretaker_phone: addForm.caretaker_phone || null,
      caretaker_relation: addForm.caretaker_relation || null,
      driver_name: addForm.driver_name || null,
      driver_phone: addForm.driver_phone || null,
      driver_vehicle_number: addForm.driver_vehicle_number || null,
      status: addForm.status || "completed",
    };

    const created = await createAdminBooking(payload);

    // 2. Upload drop proof if provided (now we have a real DB id)
    if (addDropFile && created.id) {
      try {
        await uploadDropProofAdmin(created.id, addDropFile);
      } catch (e) {
        console.error("Drop proof upload failed:", e);
        toast.warning("Booking saved, but drop photo upload failed");
      }
    }

    toast.success("Booking added successfully!");

    // 3. Re-fetch from server so table reflects DB truth
    await fetchReports();

    setShowAddForm(false);
    resetAddForm();
  } catch (err) {
    console.error(err);
    toast.error(err?.message || "Failed to add booking");
  } finally {
    setSubmittingAdd(false);
  }
};

  const filteredBookings = useMemo(() => {
    return bookings.filter((b) => {
      const patientMatch = (b.patient_name || b.booker_name || "").toLowerCase().includes(filters.patient.toLowerCase())
      const mobileMatch = (b.patient_contact || b.booker_phone || "").includes(filters.mobile)
      const driverMatch = (b.driver?.name || "").toLowerCase().includes(filters.driver.toLowerCase())

      let dateMatch = true
      if (filters.startDate && filters.endDate) {
        const bookingDate = new Date(b.created_at)
        const start = new Date(filters.startDate)
        const end = new Date(filters.endDate)
        end.setHours(23, 59, 59, 999)
        dateMatch = bookingDate >= start && bookingDate <= end
      }

      return patientMatch && mobileMatch && driverMatch && dateMatch
    }).sort((a, b) => b.id - a.id)
  }, [bookings, filters])

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-600">
          <ClipboardList className="h-6 w-6 animate-pulse text-blue-600" />
          <p className="text-lg font-medium">Loading Reports...</p>
        </div>
      </div>
    )
  }

  const exportToExcel = () => {
    if (!filteredBookings.length) return
    let data = []

    if (reportType === "ambulance") {
      data = filteredBookings.map((b) => ({
        "Booking ID": b.id,
        "Register No": b.registration_number,
        "Patient Name": b.patient_name,
        "Patient Mobile": b.patient_contact || b.booker_phone,
        "Driver Name": b.driver?.name || "N/A",
        "Driver Mobile": b.driver?.phone || "N/A",
        "Vehicle Number": b.driver?.vehicle_number || "N/A",
        "Ambulance Type": b.ambulance_type,
        Status: b.status,
        "Date & Time": b.booking_date
          ? new Date(b.booking_date).toLocaleDateString('en-IN')
          : formatDate(b.created_at),
      }))
    }

    if (reportType === "patient") {
      data = filteredBookings.map((b) => ({
        "Booking Date": b.booking_date || "N/A",
        "Booking Time": b.booking_time || "N/A",
        "Register No": b.registration_number,
        "Patient Name": b.patient_name,
        Address: b.pickup_address,
        Village: b.patient_village || "N/A",
        "Police Station": b.patient_police_station || "N/A",
        District: b.patient_district || "N/A",
        Pincode: b.patient_pincode || "N/A",
        Age: b.patient_age,
        Gender: b.patient_gender,
        "Contact No": b.patient_contact || b.booker_phone,
        "Aadhaar No": b.patient_aadhar || "N/A",
        "Medical Condition": b.medical_condition,
      }))
    }

    if (reportType === "caretaker") {
      data = filteredBookings.map((b) => ({
        "Booking ID": b.id,
        "Register No": b.registration_number,
        "Patient Name": b.patient_name,
        "Care Taker Name": b.caretaker_name || "N/A",
        "Care Taker Mobile": b.caretaker_phone || "N/A",
        Relationship: b.caretaker_relation || "N/A",
        Status: b.status,
      }))
    }

    if (reportType === "booking") {
      data = filteredBookings.map((b) => ({
        "Booking ID": b.id,
        "Register No": b.registration_number,
        "Booker Name": b.booker_name || "N/A",
        "Booker Mobile": b.booker_phone || "N/A",
        "Patient Name": b.patient_name,
        "Ambulance Type": b.ambulance_type,
        Status: b.status,
        "Date & Time": b.booking_date
          ? new Date(b.booking_date).toLocaleDateString('en-IN')
          : formatDate(b.created_at),
      }))
    }

    const worksheet = XLSX.utils.json_to_sheet(data)
    const range = XLSX.utils.decode_range(worksheet["!ref"])

    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C })
      if (!worksheet[cellAddress]) continue
      worksheet[cellAddress].s = {
        font: { bold: true, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
      }
    }

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reports")
    XLSX.writeFile(workbook, `${reportType}-report.xlsx`)
  }

  const generateCertificate = async (b) => {
    setGeneratingCertId(b.id)
    let iframe = null

    try {
      const [logoBase64, photoBase64, signatureBase64, footerBase64, dropBase64] = await Promise.all([
        toBase64(ambulanceLogo),
        toBase64(ambulancePhoto),
        toBase64("/signature.png"),
        toBase64(footerPhoto),
        toBase64(getImageUrl(b.drop_proof_url)),
      ])

      iframe = document.createElement("iframe")
      iframe.style.position = "absolute"
      iframe.style.left = "-9999px"
      iframe.style.top = "0"
      iframe.style.width = "850px"
      iframe.style.height = "1200px"
      iframe.style.border = "none"
      document.body.appendChild(iframe)

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document

      iframeDoc.open()
      iframeDoc.write(`
        <html>
          <head>
            <style>
              body {
                margin:70px;
                padding-right:50px;
                font-family: Arial, sans-serif;
                background: #ffffff;
                color: #000000;
                width: 1000px;
              }
            </style>
          </head>
          <body>
            <div style="border:4px solid #008000; border-radius:20px; padding:20px; background-color:#ffffff;">
              <div style="text-align:center; margin-bottom:15px; padding:10px; background:linear-gradient(135deg, #f0fff0, #ffffff); border-radius:10px; border:1px solid #008000;">
                <p style="margin:0; font-size:14px; font-weight:bold; color:#555555;">Dedicated in Loving Memory of</p>
                <p style="margin:4px 0 0 0; font-size:18px; font-weight:bold; color:#008000;">Late Basanta Kumar Nath</p>
              </div>
              <div style="display:flex; justify-content:space-between; align-items:center; gap:10px;">
                ${logoBase64 ? `<img src="${logoBase64}" style="width:180px; height:180px; border-radius:50%; object-fit:cover;" />` : `<div style="width:180px; height:180px; border-radius:50%; background:#f0f0f0;"></div>`}
                <div style="text-align:center;flex:1;color:#000000;">
                  <h2 style="color:#008000;font-size:30px;margin:0;">FREE</h2>
                  <h2 style="color:#FF0000;font-size:30px;margin:0;">AMBULANCE</h2>
                  <h2 style="color:#008000;margin-top:10px;">SERVICE CERTIFICATE</h2>
                  <h3 style="margin:0px;padding:0px;">Dharmadaspur, Mahanga, Cuttack, Pin:754204</h3>
                  <h3 style="margin:5px;padding:5px;">📞 9776696669, 9348616669, 9006706355</h3>
                </div>
                ${photoBase64 ? `<img src="${photoBase64}" style="width:220px; height:220px; border-radius:20px; object-fit:cover;padding-right:10px;" />` : `<div style="width:220px; height:220px; border-radius:20px; background:#f0f0f0;"></div>`}
              </div>
              <div style="margin-top:10px; border:2px solid #008000; border-radius:15px; padding:20px; color:#000000;">
                <div style="display:flex; justify-content:space-between; gap:20px; align-items:flex-start;">
                  <div style="flex:2;">
                    <p><strong>Date:</strong> ${b.booking_date ? new Date(b.booking_date).toLocaleDateString('en-IN') : b.created_at ? new Date(b.created_at).toLocaleDateString('en-IN') : "N/A"}</p>
                    <p><strong>Service ID:</strong> ${b.id}</p>
                    <p><strong>Patient Name:</strong> <span style="font-size:18px; font-weight:900; color:#000000; text-transform:uppercase;">${b.patient_name || "N/A"}</span></p>
                    <p style="margin:0; font-size:14px; color:#333333;"><strong>Contact Number:</strong> ${b.patient_contact || b.booker_phone || "N/A"}</p>
                    <p><strong>Village:</strong> ${b.patient_village || "N/A"}</p>
                    <p><strong>Police Station:</strong> ${b.patient_police_station || "N/A"}</p>
                    <p><strong>District:</strong> ${b.patient_district || "N/A"}</p>
                    <p><strong>Pincode:</strong> ${b.patient_pincode || "N/A"}</p>
                    <p><strong>Drop Location:</strong> ${b.drop_address || "N/A"}</p>
                    <p><strong>Driver Name:</strong> <span style="font-size:18px; font-weight:900; color:#000000; text-transform:uppercase;">${b.driver?.name || "N/A"}</span></p>
                    <p><strong>Vehicle Number:</strong> ${b.driver?.vehicle_number || "N/A"}</p>
                    <p><strong>Ambulance Type:</strong> ${b.ambulance_type || "N/A"}</p>
                  </div>
                  ${dropBase64 ? `
                  <div style="flex:1; display:flex; flex-direction:column; gap:12px; align-items:center; margin-top:70px;">
                    <div style="text-align:center;">
                      <p style="margin:0 0 5px 0; font-size:13px; font-weight:bold;">Drop Photo</p>
                      <img src="${dropBase64}" style="width:200px; height:200px; object-fit:cover; border-radius:8px; border:1px solid #ccc;" />
                    </div>
                  </div>` : ''}
                </div>
                <div style="margin-top:20px; padding:10px; border:2px dashed #008000; font-size:24px; font-weight:bold; color:#FF0000; text-align:center; border-radius:10px;">
                  ₹0 (FREE SERVICE)
                </div>
                <p style="margin-top:15px; text-align:center; font-size:16px; color:#333333;">
                  This ambulance service is provided completely free of cost as a charitable service to the community.
                </p>
              </div>
              <div style="margin-top:40px; display:flex; justify-content:flex-end; padding:0 40px;">
               <div style="text-align:center; width:45%;">
                  ${signatureBase64 ? `<img src="${signatureBase64}" style="width:160px; height:70px; object-fit:contain; margin-bottom:-10px;" />` : `<div style="width:160px; height:70px;"></div>`}
                  <div style="border-bottom:2px solid #000; width:100%; margin-bottom:8px;"></div>
                  <p style="margin:0; font-weight:bold; font-size:16px;">Founder Signature</p>
                  <p style="margin:4px 0 0 0; color:#555; font-size:13px;">Nagendra Nath</p>
                </div>
              </div>
              <div style="margin-top:30px; border-radius:15px; overflow:hidden; border:2px solid #008000; position:relative;">
                ${footerBase64 ? `<img src="${footerBase64}" style="width:100%; height:120px; object-fit:cover; display:block;" />` : `<div style="width:100%; height:120px; background:#f0f0f0;"></div>`}
                <div style="position:absolute; bottom:0; left:0; right:0; background:linear-gradient(transparent, rgba(0,128,0,0.9)); padding:6px; text-align:center; color:#ffffff;">
                  <p style="margin:0; font-size:16px; font-weight:bold; letter-spacing:1px;">FOR EMERGENCY &amp; FREE AMBULANCE SERVICE</p>
                  <p style="margin:3px 0 0 0;font-weight:bold; font-size:15px; opacity:0.9;">Available 24/7 • Completely Free • Serving the Community</p>
                </div>
              </div>
              <div style="margin-top:15px; text-align:center; padding:5px; background:#f0fff0; border-radius:10px; border:1px dashed #008000;">
                <p style="margin:0; margin-bottom:13px; text-align:center; font-size:27px; font-weight:bold; color:#008000;">
                  🕯️In Loving Memory of Late Basanta Kumar Nath — His vision of free healthcare for all lives on through this service🕯️
                </p>
              </div>
            </div>
          </body>
        </html>
      `)
      iframeDoc.close()

      const images = iframeDoc.querySelectorAll("img")
      await Promise.all(
        Array.from(images).map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete && img.naturalHeight > 0) resolve()
              else { img.onload = resolve; img.onerror = resolve }
            })
        )
      )

      await new Promise((r) => setTimeout(r, 500))

      const elementToCapture = iframeDoc.body
      const canvas = await html2canvas(elementToCapture, {
        scale: 2, useCORS: true, allowTaint: true, backgroundColor: "#ffffff", logging: false,
      })

      const imgData = canvas.toDataURL("image/png")
      const pdf = new jsPDF("p", "mm", "a4")
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgHeight = (canvas.height * pdfWidth) / canvas.width
      const finalHeight = imgHeight > pdfHeight ? pdfHeight : imgHeight
      const finalWidth = imgHeight > pdfHeight ? (pdfHeight * pdfWidth) / imgHeight : pdfWidth

      pdf.addImage(imgData, "PNG", 0, 0, finalWidth, finalHeight)
      pdf.save(`${b.patient_name || "certificate"}-certificate.pdf`)
      toast.success("Certificate downloaded successfully!")
    } catch (error) {
      console.error("Certificate generation error:", error)
      toast.error("Failed to generate certificate")
    } finally {
      if (iframe && iframe.parentNode) document.body.removeChild(iframe)
      setGeneratingCertId(null)
    }
  }

  const reportTabs = [
    { value: "ambulance", label: "Ambulance", icon: Ambulance },
    { value: "patient", label: "Patient", icon: HeartPulse },
    { value: "caretaker", label: "Care Taker", icon: UserRound },
    { value: "booking", label: "Booking Person", icon: ClipboardList },
  ]

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl space-y-6">

        {/* HEADER — Plus button added here */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white p-5 rounded-xl shadow-sm border border-slate-100">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                <FileText className="h-5 w-5" />
              </div>
              Booking Reports
            </h1>
            <p className="text-sm text-slate-500 mt-1 ml-10">Filter, view, and export booking data</p>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {/* ✅ PLUS BUTTON */}
            <Button
              onClick={() => { resetAddForm(); setShowAddForm(true) }}
              className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none shadow-md shadow-blue-200 transition-all hover:shadow-lg hover:shadow-blue-200"
            >
              <Plus className="h-4 w-4 mr-2" /> Add New
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin")} className="flex-1 sm:flex-none">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <Button onClick={exportToExcel} className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none">
              <FileDown className="h-4 w-4 mr-2" /> Export Excel
            </Button>
          </div>
        </div>

        {/* REPORT TYPE TABS */}
        <div className="bg-slate-100 p-1.5 rounded-xl inline-flex gap-1 flex-wrap">
          {reportTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setReportType(tab.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${reportType === tab.value
                ? "bg-white text-blue-700 shadow-sm"
                : "text-slate-600 hover:text-slate-800 hover:bg-slate-200"
                }`}
            >
              <tab.icon className="h-4 w-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* FILTERS */}
        <Card className="border-slate-100 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3 text-slate-600">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">Filter Reports</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
              <Input placeholder="Patient Name" value={filters.patient} onChange={(e) => setFilters({ ...filters, patient: e.target.value })} className="bg-slate-50 border-slate-200" />
              <Input placeholder="Mobile Number" value={filters.mobile} onChange={(e) => setFilters({ ...filters, mobile: e.target.value })} className="bg-slate-50 border-slate-200" />
              <Input placeholder="Driver Name" value={filters.driver} onChange={(e) => setFilters({ ...filters, driver: e.target.value })} className="bg-slate-50 border-slate-200" />
              <Input type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} className="bg-slate-50 border-slate-200" />
              <Input type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} className="bg-slate-50 border-slate-200" />
            </div>
          </CardContent>
        </Card>

        {/* TABLE */}
        <Card className="border-slate-100 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                {reportType === "ambulance" && (
                  <>
                    <TableHeader className="bg-slate-50 border-b border-slate-100">
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Booking ID</TableHead>
                        <TableHead className="whitespace-nowrap">Register No</TableHead>
                        <TableHead className="whitespace-nowrap">Patient Name</TableHead>
                        <TableHead className="whitespace-nowrap">Patient Mobile</TableHead>
                        <TableHead className="whitespace-nowrap">Driver Name</TableHead>
                        <TableHead className="whitespace-nowrap">Driver Mobile</TableHead>
                        <TableHead className="whitespace-nowrap">Vehicle No</TableHead>
                        <TableHead className="whitespace-nowrap">Amb. Type</TableHead>
                        <TableHead className="whitespace-nowrap">Drop Photo</TableHead>
                        <TableHead className="whitespace-nowrap">Status</TableHead>
                        <TableHead className="whitespace-nowrap">Date & Time</TableHead>
                        <TableHead className="whitespace-nowrap">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBookings.length > 0 ? (
                        filteredBookings.map((b) => (
                          <TableRow key={b.id} className="hover:bg-slate-50/50">
                            <TableCell className="font-medium">{b.id}</TableCell>
                            <TableCell className="whitespace-nowrap">{b.registration_number}</TableCell>
                            <TableCell className="font-medium text-slate-800 whitespace-nowrap">{b.patient_name}</TableCell>
                            <TableCell className="whitespace-nowrap">{b.patient_contact || b.booker_phone}</TableCell>
                            <TableCell className="whitespace-nowrap">{b.driver?.name || "N/A"}</TableCell>
                            <TableCell className="whitespace-nowrap">{b.driver?.phone || "N/A"}</TableCell>
                            <TableCell className="whitespace-nowrap">{b.driver?.vehicle_number || "N/A"}</TableCell>
                            <TableCell><Badge variant="outline" className="bg-slate-50">{b.ambulance_type}</Badge></TableCell>
                            <TableCell>
                              {b.drop_proof_url ? (
                                <img src={getImageUrl(b.drop_proof_url)} alt="Drop" className="h-14 w-14 rounded-md object-cover cursor-pointer border border-slate-200 hover:opacity-80 transition-opacity shadow-sm" onClick={() => setSelectedImage(getImageUrl(b.drop_proof_url))} />
                              ) : (
                                <div className="h-14 w-14 rounded-md bg-slate-100 flex items-center justify-center text-slate-400"><ImageOff className="h-5 w-5" /></div>
                              )}
                            </TableCell>
                            <TableCell><Badge variant="outline" className={getStatusColor(b.status)}>{b.status}</Badge></TableCell>
                            <TableCell className="whitespace-nowrap text-xs text-slate-600">
                              {b.booking_date ? new Date(b.booking_date).toLocaleDateString('en-IN') : formatDate(b.created_at)}
                            </TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-800 hover:bg-blue-50" onClick={() => openEditDialog(b)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow><TableCell colSpan={12} className="h-24 text-center text-slate-500">No reports found.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </>
                )}

                {reportType === "patient" && (
                  <>
                    <TableHeader className="bg-slate-50 border-b border-slate-100">
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Booking Id</TableHead>
                        <TableHead className="whitespace-nowrap">Date</TableHead>
                        <TableHead className="whitespace-nowrap">Time</TableHead>
                        <TableHead className="whitespace-nowrap">Register No</TableHead>
                        <TableHead className="whitespace-nowrap">Patient Name</TableHead>
                        <TableHead className="whitespace-nowrap">Address</TableHead>
                        <TableHead className="whitespace-nowrap">Village</TableHead>
                        <TableHead className="whitespace-nowrap">Police Station</TableHead>
                        <TableHead className="whitespace-nowrap">District</TableHead>
                        <TableHead className="whitespace-nowrap">Pincode</TableHead>
                        <TableHead className="whitespace-nowrap">Age</TableHead>
                        <TableHead className="whitespace-nowrap">Gender</TableHead>
                        <TableHead className="whitespace-nowrap">Contact No</TableHead>
                        <TableHead className="whitespace-nowrap">Aadhaar</TableHead>
                        <TableHead className="whitespace-nowrap">Condition</TableHead>
                        <TableHead className="whitespace-nowrap">Certificate</TableHead>
                        <TableHead className="whitespace-nowrap">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBookings.length > 0 ? (
                        filteredBookings.map((b) => (
                          <TableRow key={b.id} className="hover:bg-slate-50/50">
                            <TableCell>{b.id}</TableCell>
                            <TableCell className="whitespace-nowrap">{b.booking_date || "N/A"}</TableCell>
                            <TableCell>{b.booking_time || "N/A"}</TableCell>
                            <TableCell>{b.registration_number}</TableCell>
                            <TableCell className="font-medium text-slate-800 whitespace-nowrap">{b.patient_name}</TableCell>
                            <TableCell className="max-w-[150px] truncate">{b.pickup_address}</TableCell>
                            <TableCell>{b.patient_village || "N/A"}</TableCell>
                            <TableCell>{b.patient_police_station || "N/A"}</TableCell>
                            <TableCell>{b.patient_district || "N/A"}</TableCell>
                            <TableCell>{b.patient_pincode || "N/A"}</TableCell>
                            <TableCell>{b.patient_age}</TableCell>
                            <TableCell>{b.patient_gender}</TableCell>
                            <TableCell>{b.patient_contact || b.booker_phone}</TableCell>
                            <TableCell>{b.patient_aadhar || "N/A"}</TableCell>
                            <TableCell className="max-w-[150px] truncate">{b.medical_condition}</TableCell>
                            <TableCell>
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => generateCertificate(b)} disabled={generatingCertId === b.id}>
                                {generatingCertId === b.id ? "Wait..." : "PDF"}
                              </Button>
                            </TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-800 hover:bg-blue-50" onClick={() => openEditDialog(b)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow><TableCell colSpan={16} className="h-24 text-center text-slate-500">No patient reports found.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </>
                )}

                {reportType === "caretaker" && (
                  <>
                    <TableHeader className="bg-slate-50 border-b border-slate-100">
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Booking ID</TableHead>
                        <TableHead className="whitespace-nowrap">Register No</TableHead>
                        <TableHead className="whitespace-nowrap">Patient Name</TableHead>
                        <TableHead className="whitespace-nowrap">Care Taker Name</TableHead>
                        <TableHead className="whitespace-nowrap">Care Taker Mobile</TableHead>
                        <TableHead className="whitespace-nowrap">Relationship</TableHead>
                        <TableHead className="whitespace-nowrap">Status</TableHead>
                        <TableHead className="whitespace-nowrap">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBookings.length > 0 ? (
                        filteredBookings.map((b) => (
                          <TableRow key={b.id} className="hover:bg-slate-50/50">
                            <TableCell className="font-medium">{b.id}</TableCell>
                            <TableCell>{b.registration_number}</TableCell>
                            <TableCell className="font-medium text-slate-800 whitespace-nowrap">{b.patient_name}</TableCell>
                            <TableCell>{b.caretaker_name || "N/A"}</TableCell>
                            <TableCell>{b.caretaker_phone || "N/A"}</TableCell>
                            <TableCell>{b.caretaker_relation || "N/A"}</TableCell>
                            <TableCell><Badge variant="outline" className={getStatusColor(b.status)}>{b.status}</Badge></TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-800 hover:bg-blue-50" onClick={() => openEditDialog(b)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow><TableCell colSpan={8} className="h-24 text-center text-slate-500">No caretaker reports found.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </>
                )}

                {reportType === "booking" && (
                  <>
                    <TableHeader className="bg-slate-50 border-b border-slate-100">
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Booking ID</TableHead>
                        <TableHead className="whitespace-nowrap">Register No</TableHead>
                        <TableHead className="whitespace-nowrap">Booker Name</TableHead>
                        <TableHead className="whitespace-nowrap">Booker Mobile</TableHead>
                        <TableHead className="whitespace-nowrap">Patient Name</TableHead>
                        <TableHead className="whitespace-nowrap">Amb. Type</TableHead>
                        <TableHead className="whitespace-nowrap">Status</TableHead>
                        <TableHead className="whitespace-nowrap">Date & Time</TableHead>
                        <TableHead className="whitespace-nowrap">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBookings.length > 0 ? (
                        filteredBookings.map((b) => (
                          <TableRow key={b.id} className="hover:bg-slate-50/50">
                            <TableCell className="font-medium">{b.id}</TableCell>
                            <TableCell>{b.registration_number}</TableCell>
                            <TableCell>{b.booker_name || "N/A"}</TableCell>
                            <TableCell>{b.booker_phone || "N/A"}</TableCell>
                            <TableCell className="font-medium text-slate-800 whitespace-nowrap">{b.patient_name}</TableCell>
                            <TableCell>{b.ambulance_type}</TableCell>
                            <TableCell><Badge variant="outline" className={getStatusColor(b.status)}>{b.status}</Badge></TableCell>
                            <TableCell className="whitespace-nowrap text-xs text-slate-600">
                              {b.booking_date ? new Date(b.booking_date).toLocaleDateString('en-IN') : formatDate(b.created_at)}
                            </TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-800 hover:bg-blue-50" onClick={() => openEditDialog(b)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow><TableCell colSpan={9} className="h-24 text-center text-slate-500">No booking person reports found.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </>
                )}
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* TOTAL COUNTER */}
        <div className="flex justify-end">
          <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-100 text-sm font-medium text-slate-600">
            Total Results: <span className="text-blue-600 font-bold">{filteredBookings.length}</span>
          </div>
        </div>

      </div>

      {/* IMAGE PREVIEW DIALOG */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-3xl p-2 bg-white">
          <DialogHeader>
            <DialogTitle className="text-center">Image Preview</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="flex justify-center">
              <img src={selectedImage} alt="Preview" className="max-h-[80vh] w-auto rounded-lg object-contain" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* EDIT BOOKING DIALOG */}
      <Dialog open={!!editingBooking} onOpenChange={(isOpen) => { if (!isOpen) { setEditingBooking(null); setEditDropFile(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>Edit Booking #{editingBooking?.id}</DialogTitle>
          </DialogHeader>
          {editingBooking && (
            <div className="space-y-4 pt-4">
              <div className="space-y-3 border p-4 rounded-lg bg-slate-50">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Booking Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-slate-600">Booker Name</label><Input value={editingBooking.booker_name || ""} onChange={(e) => handleEditChange("booker_name", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Booker Phone</label><Input value={editingBooking.booker_phone || ""} onChange={(e) => handleEditChange("booker_phone", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Booking Date</label><Input type="date" value={editingBooking.booking_date || ""} onChange={(e) => handleEditChange("booking_date", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Booking Time</label><Input type="time" value={editingBooking.booking_time || ""} onChange={(e) => handleEditChange("booking_time", e.target.value)} /></div>
                </div>
              </div>
              <div className="space-y-3 border p-4 rounded-lg bg-slate-50">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2"><UserRound className="h-4 w-4" /> Patient Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-slate-600">Patient Name</label><Input value={editingBooking.patient_name || ""} onChange={(e) => handleEditChange("patient_name", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Contact Number</label><Input value={editingBooking.patient_contact || ""} onChange={(e) => handleEditChange("patient_contact", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Age</label><Input type="number" value={editingBooking.patient_age || ""} onChange={(e) => handleEditChange("patient_age", e.target.value)} /></div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Gender</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editingBooking.patient_gender || ""} onChange={(e) => handleEditChange("patient_gender", e.target.value)}>
                      <option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="space-y-3 border p-4 rounded-lg bg-slate-50">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2"><HeartPulse className="h-4 w-4" /> Location & Medical</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="col-span-1 sm:col-span-2"><label className="text-xs font-medium text-slate-600">Pickup Address</label><Input value={editingBooking.pickup_address || ""} onChange={(e) => handleEditChange("pickup_address", e.target.value)} /></div>
                  <div className="col-span-1 sm:col-span-2"><label className="text-xs font-medium text-slate-600">Drop Address</label><Input value={editingBooking.drop_address || ""} onChange={(e) => handleEditChange("drop_address", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Village</label><Input value={editingBooking.patient_village || ""} onChange={(e) => handleEditChange("patient_village", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Police Station</label><Input value={editingBooking.patient_police_station || ""} onChange={(e) => handleEditChange("patient_police_station", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">District</label><Input value={editingBooking.patient_district || ""} onChange={(e) => handleEditChange("patient_district", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Pincode</label><Input value={editingBooking.patient_pincode || ""} onChange={(e) => handleEditChange("patient_pincode", e.target.value)} /></div>
                  <div className="col-span-1 sm:col-span-2"><label className="text-xs font-medium text-slate-600">Medical Condition</label><Input value={editingBooking.medical_condition || ""} onChange={(e) => handleEditChange("medical_condition", e.target.value)} /></div>
                </div>
              </div>
              <div className="space-y-3 border p-4 rounded-lg bg-slate-50">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2"><UserRound className="h-4 w-4" /> Caretaker Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-slate-600">Caretaker Name</label><Input value={editingBooking.caretaker_name || ""} onChange={(e) => handleEditChange("caretaker_name", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Caretaker Phone</label><Input value={editingBooking.caretaker_phone || ""} onChange={(e) => handleEditChange("caretaker_phone", e.target.value)} /></div>
                  <div><label className="text-xs font-medium text-slate-600">Relation</label><Input value={editingBooking.caretaker_relation || ""} onChange={(e) => handleEditChange("caretaker_relation", e.target.value)} /></div>
                </div>
              </div>
              <div className="space-y-3 border p-4 rounded-lg bg-slate-50">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Camera className="h-4 w-4" /> Drop Photo</h3>
                <div className="flex items-center gap-4">
                  {editingBooking.drop_proof_url ? (
                    <img src={getImageUrl(editingBooking.drop_proof_url)} alt="Current Drop" className="h-20 w-20 rounded-md object-cover border border-slate-200 shadow-sm" />
                  ) : (
                    <div className="h-20 w-20 rounded-md bg-slate-200 flex items-center justify-center text-slate-500"><ImageOff className="h-8 w-8" /></div>
                  )}
                  <div className="flex-1">
                    <label className="text-xs font-medium text-slate-600">Upload New Photo (Optional)</label>
                    <Input type="file" accept="image/*" onChange={(e) => setEditDropFile(e.target.files[0] || null)} className="mt-1" />
                    {editDropFile && <p className="text-xs text-emerald-600 mt-1 font-medium">Selected: {editDropFile.name}</p>}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setEditingBooking(null); setEditDropFile(null); }}>Cancel</Button>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSaveEdit} disabled={savingEdit}>{savingEdit ? "Saving..." : "Save Changes"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ✅✅✅ ADD NEW BOOKING DIALOG ✅✅✅ */}
      <Dialog open={showAddForm} onOpenChange={(isOpen) => { if (!isOpen) { setShowAddForm(false); resetAddForm(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <div className="p-1.5 bg-blue-100 rounded-lg text-blue-600">
                <Plus className="h-5 w-5" />
              </div>
              Add New Booking
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">

            {/* ── Section 1: Booking Details ── */}
            <div className="space-y-3 border border-blue-100 p-4 rounded-lg bg-blue-50/40">
              <h3 className="font-semibold text-blue-800 flex items-center gap-2 text-sm">
                <ClipboardList className="h-4 w-4" /> Booking Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Register No <span className="text-red-400">*</span></label>
                  <Input placeholder="e.g. REG-001" value={addForm.registration_number} onChange={(e) => handleAddChange("registration_number", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Ambulance Type</label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm" value={addForm.ambulance_type} onChange={(e) => handleAddChange("ambulance_type", e.target.value)}>
                    <option value="Basic">Basic</option>
                    <option value="Advanced">Advanced</option>
                    <option value="ICU">ICU</option>
                    <option value="Oxygen">Oxygen</option>
                    <option value="Dead Body">Dead Body</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Booking Date</label>
                  <Input type="date" value={addForm.booking_date} onChange={(e) => handleAddChange("booking_date", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Booking Time</label>
                  <Input type="time" value={addForm.booking_time} onChange={(e) => handleAddChange("booking_time", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Booker Name</label>
                  <Input placeholder="Who booked?" value={addForm.booker_name} onChange={(e) => handleAddChange("booker_name", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Booker Phone</label>
                  <Input placeholder="Booker mobile" value={addForm.booker_phone} onChange={(e) => handleAddChange("booker_phone", e.target.value)} className="bg-white" />
                </div>
              </div>
            </div>

            {/* ── Section 2: Patient Details ── */}
            <div className="space-y-3 border border-emerald-100 p-4 rounded-lg bg-emerald-50/40">
              <h3 className="font-semibold text-emerald-800 flex items-center gap-2 text-sm">
                <HeartPulse className="h-4 w-4" /> Patient Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Patient Name <span className="text-red-400">*</span></label>
                  <Input placeholder="Full name" value={addForm.patient_name} onChange={(e) => handleAddChange("patient_name", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Contact Number <span className="text-red-400">*</span></label>
                  <Input placeholder="Mobile number" value={addForm.patient_contact} onChange={(e) => handleAddChange("patient_contact", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Age</label>
                  <Input type="number" placeholder="Age" value={addForm.patient_age} onChange={(e) => handleAddChange("patient_age", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Gender</label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm" value={addForm.patient_gender} onChange={(e) => handleAddChange("patient_gender", e.target.value)}>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Aadhaar No</label>
                  <Input placeholder="Aadhaar number" value={addForm.patient_aadhar} onChange={(e) => handleAddChange("patient_aadhar", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Medical Condition</label>
                  <Input placeholder="Condition / symptoms" value={addForm.medical_condition} onChange={(e) => handleAddChange("medical_condition", e.target.value)} className="bg-white" />
                </div>
              </div>
            </div>

            {/* ── Section 3: Location Details ── */}
            <div className="space-y-3 border border-amber-100 p-4 rounded-lg bg-amber-50/40">
              <h3 className="font-semibold text-amber-800 flex items-center gap-2 text-sm">
                <Ambulance className="h-4 w-4" /> Pickup & Drop Location
              </h3>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Pickup Address</label>
                  <Input placeholder="Full pickup address" value={addForm.pickup_address} onChange={(e) => handleAddChange("pickup_address", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Drop Address</label>
                  <Input placeholder="Hospital / destination" value={addForm.drop_address} onChange={(e) => handleAddChange("drop_address", e.target.value)} className="bg-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Village</label>
                  <Input placeholder="Village" value={addForm.patient_village} onChange={(e) => handleAddChange("patient_village", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Police Station</label>
                  <Input placeholder="PS" value={addForm.patient_police_station} onChange={(e) => handleAddChange("patient_police_station", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">District</label>
                  <Input placeholder="District" value={addForm.patient_district} onChange={(e) => handleAddChange("patient_district", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Pincode</label>
                  <Input placeholder="Pin" value={addForm.patient_pincode} onChange={(e) => handleAddChange("patient_pincode", e.target.value)} className="bg-white" />
                </div>
              </div>
            </div>

            {/* ── Section 4: Driver Details ── */}
            <div className="space-y-3 border border-violet-100 p-4 rounded-lg bg-violet-50/40">
              <h3 className="font-semibold text-violet-800 flex items-center gap-2 text-sm">
                <Ambulance className="h-4 w-4" /> Driver Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Driver Name</label>
                  <Input placeholder="Driver full name" value={addForm.driver_name} onChange={(e) => handleAddChange("driver_name", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Driver Phone</label>
                  <Input placeholder="Driver mobile" value={addForm.driver_phone} onChange={(e) => handleAddChange("driver_phone", e.target.value)} className="bg-white" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-slate-600">Vehicle Number</label>
                  <Input placeholder="e.g. OD-05-AB-1234" value={addForm.driver_vehicle_number} onChange={(e) => handleAddChange("driver_vehicle_number", e.target.value)} className="bg-white" />
                </div>
              </div>
            </div>

            {/* ── Section 5: Caretaker Details ── */}
            <div className="space-y-3 border border-rose-100 p-4 rounded-lg bg-rose-50/40">
              <h3 className="font-semibold text-rose-800 flex items-center gap-2 text-sm">
                <UserRound className="h-4 w-4" /> Caretaker Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Caretaker Name</label>
                  <Input placeholder="Caretaker name" value={addForm.caretaker_name} onChange={(e) => handleAddChange("caretaker_name", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Caretaker Phone</label>
                  <Input placeholder="Caretaker mobile" value={addForm.caretaker_phone} onChange={(e) => handleAddChange("caretaker_phone", e.target.value)} className="bg-white" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-slate-600">Relationship with Patient</label>
                  <Input placeholder="e.g. Son, Daughter, Spouse" value={addForm.caretaker_relation} onChange={(e) => handleAddChange("caretaker_relation", e.target.value)} className="bg-white" />
                </div>
              </div>
            </div>

            {/* ── Section 6: Drop Photo (Optional) ── */}
            <div className="space-y-3 border border-slate-200 p-4 rounded-lg bg-slate-50/60">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2 text-sm">
                <Camera className="h-4 w-4" /> Drop Photo (Optional)
              </h3>
              <Input type="file" accept="image/*" onChange={(e) => setAddDropFile(e.target.files[0] || null)} className="bg-white" />
              {addDropFile && (
                <div className="flex items-center gap-3 mt-2">
                  <img src={URL.createObjectURL(addDropFile)} alt="Preview" className="h-16 w-16 rounded-md object-cover border border-slate-200" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">{addDropFile.name}</p>
                    <p className="text-xs text-slate-500">{(addDropFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button onClick={() => setAddDropFile(null)} className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {/* ── Submit / Cancel ── */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => { setShowAddForm(false); resetAddForm(); }}>
                Cancel
              </Button>
              <Button
                onClick={handleAddSubmit}
                disabled={submittingAdd}
                className="bg-blue-600 hover:bg-blue-700 min-w-[140px] shadow-md shadow-blue-200"
              >
                {submittingAdd ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Adding...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Plus className="h-4 w-4" /> Add Booking
                  </span>
                )}
              </Button>
            </div>

          </div>
        </DialogContent>
      </Dialog>

    </div>
  )
}